import { Link } from 'react-router-dom';

import { GenericCatalogTable } from '../components/generic-catalog-table';
import { ScrollReveal } from '../components/scroll-reveal';
import { TECHNOLOGY_CATEGORIES } from '../data/technology-mapping';

export default function TechnologyMapping() {
  return (
    <div>
      <section className="relative overflow-hidden py-16" style={{ background: "var(--gradient-hero)" }}>
        <div className="pointer-events-none absolute inset-0">
          <div className="aurora" />
          <div className="noise" />
        </div>

        <div className="container-ivalue relative">
          <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
            Technology Mapping
          </div>
          <h1 className="mt-4 text-balance text-4xl font-extrabold tracking-[-0.03em] text-text-primary md:text-6xl">
            Regulation → Control Domain → Control →
            <br />
            Technology → Vendor → Evidence
          </h1>
          <p className="mt-5 max-w-3xl text-lg leading-relaxed text-text-secondary">
            A unified mapping layer that translates regulatory control obligations into tool capabilities and evidence artifacts.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <a
              href="#categories"
              className="focus-ring sheen inline-flex h-12 items-center justify-center rounded-xl bg-gradient-to-r from-[#10B981] to-[#059669] px-5 text-sm font-semibold text-white transition-transform hover:scale-[1.02] active:scale-[0.98]"
            >
              Explore categories
            </a>
            <Link
              to="/dashboards"
              className="focus-ring inline-flex h-12 items-center justify-center rounded-xl border border-border-subtle bg-white/5 px-5 text-sm font-semibold text-text-primary transition-colors hover:bg-white/10"
            >
              View dashboards
            </Link>
            <Link
              to="/frameworks/india/rbi"
              className="focus-ring inline-flex h-12 items-center justify-center rounded-xl px-5 text-sm font-semibold text-[color:var(--text-link)] transition-colors hover:text-text-primary"
            >
              Start with RBI →
            </Link>
          </div>

          <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { k: "Frameworks mapped", v: "9+" },
              { k: "Categories", v: String(TECHNOLOGY_CATEGORIES.length) },
              { k: "Automation leverage", v: "70–90%" },
              { k: "Evidence types", v: "Logs / Reports / Proof" },
            ].map((s) => (
              <div key={s.k} className="glass-card sheen p-5">
                <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                  {s.k}
                </div>
                <div className="mt-2 font-[family:var(--font-space-grotesk)] text-2xl font-semibold text-text-primary">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-primary py-20" id="categories">
        <div className="container-ivalue">
          <ScrollReveal>
            <div>
              <h2 className="text-3xl font-extrabold tracking-[-0.02em] text-text-primary md:text-5xl">
                Technology category cards
              </h2>
              <p className="mt-3 max-w-3xl text-lg text-text-secondary">
                Each category shows the controls it supports, the evidence it generates, and the automation potential.
              </p>
            </div>
          </ScrollReveal>

          <div className="mt-10 grid gap-4 lg:grid-cols-2">
            {TECHNOLOGY_CATEGORIES.slice(0, 6).map((c) => (
              <div key={c.category} className="glass-card sheen p-6">
                <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                  Category
                </div>
                <div className="mt-2 text-xl font-semibold text-text-primary">
                  {c.category}
                </div>
                <div className="mt-4 space-y-2">
                  <div className="rounded-xl border border-border-subtle bg-white/5 px-3 py-2 text-sm text-text-secondary">
                    Regulatory controls addressed:
                    <div className="mt-1 text-xs text-text-tertiary">{c.controlsAddressed}</div>
                  </div>
                  <div className="rounded-xl border border-border-subtle bg-white/5 px-3 py-2 text-sm text-text-secondary">
                    OEMs:
                    <div className="mt-1 text-xs text-text-tertiary">{c.keyVendors}</div>
                  </div>
                  <div className="rounded-xl border border-border-subtle bg-white/5 px-3 py-2 text-sm text-text-secondary">
                    Evidence types generated:
                    <div className="mt-1 text-xs text-text-tertiary">
                      Logs, dashboards, incident timelines, control attestations
                    </div>
                  </div>
                  <div className="rounded-xl border border-border-subtle bg-white/5 px-3 py-2 text-sm text-text-secondary">
                    Automation potential:
                    <div className="mt-2 h-2 w-full rounded-full bg-white/5">
                      <div
                        className="h-2 rounded-full bg-gradient-to-r from-[#10B981] to-[#3B82F6]"
                        style={{ width: `${c.automationPercent}%` }}
                      />
                    </div>
                    <div className="mt-1 text-xs text-text-tertiary">{c.automationPercent}%</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-secondary py-20" id="catalog">
        <div className="container-ivalue">
          <ScrollReveal>
            <div>
              <h2 className="text-3xl font-extrabold tracking-[-0.02em] text-text-primary md:text-5xl">
                Mapping catalog
              </h2>
              <p className="mt-3 max-w-3xl text-lg text-text-secondary">
                Searchable view of the technology categories and their control coverage.
              </p>
            </div>
          </ScrollReveal>

          <div className="mt-10">
            <GenericCatalogTable
              data={TECHNOLOGY_CATEGORIES}
              columns={[
                { accessorKey: "category", header: "Category", size: 180 },
                { accessorKey: "keyVendors", header: "OEMs", size: 260 },
                { accessorKey: "controlsAddressed", header: "Controls Addressed", size: 520 },
                { accessorKey: "automationPercent", header: "Automation %", size: 140 },
              ]}
              csvColumns={[
                { header: "Category", accessorKey: "category" },
                { header: "OEMs", accessorKey: "keyVendors" },
                { header: "Controls Addressed", accessorKey: "controlsAddressed" },
                { header: "Automation %", accessorKey: "automationPercent" },
              ]}
            />
          </div>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/frameworks/india/cert-in"
              className="focus-ring inline-flex h-11 items-center justify-center rounded-xl border border-border-subtle bg-white/5 px-4 text-sm font-semibold text-text-primary hover:bg-white/10"
            >
              Open CERT-In catalog →
            </Link>
            <Link
              to="/frameworks/international/iso-27001"
              className="focus-ring inline-flex h-11 items-center justify-center rounded-xl border border-border-subtle bg-white/5 px-4 text-sm font-semibold text-text-primary hover:bg-white/10"
            >
              Open ISO 27001 →
            </Link>
            <Link
              to="/dashboards"
              className="focus-ring inline-flex h-11 items-center justify-center rounded-xl bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8] px-4 text-sm font-semibold text-white hover:opacity-95"
            >
              See dashboard templates
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
